import { toast } from "sonner";
import React from "react";
import { api } from "@/lib/api";
import { useNav, VIEWS } from "@/lib/nav";

// Match "see page 117", "(see page 94)", "page 122", "see \"Damage Types\" page 94".
// Captures the page number. Case-insensitive.
const PAGE_REGEX = /\bpage\s+(\d{1,3})\b/gi;

/**
 * Renders text with "see page X" / "page X" auto-linked. Clicking a page
 * link jumps the PDF viewer (when the page is inside the current section)
 * or navigates to the section that contains it.
 *
 * Props:
 *   text          string to render
 *   source        "rulebook" | "steamroller" — passed to the page-to-section API
 *   currentPages  array of 1-indexed page numbers the current viewer is showing
 *   onJumpInside  (page) => void  — called when target page IS in currentPages
 *   pdfTotalPages (number) — used to gray-out refs that exceed this PDF
 *                  (the abridged rulebook references pages >86 that don't exist).
 *   className     wrapper class
 */
export function LinkifiedText({ text, source = "rulebook", currentPages = [], onJumpInside, pdfTotalPages, className = "" }) {
  const { navigate } = useNav();
  if (!text) return null;

  const pagesSet = new Set(currentPages);

  const handleClick = async (page) => {
    if (pagesSet.has(page)) {
      onJumpInside && onJumpInside(page);
      return;
    }
    // Otherwise — find the section containing this page and navigate there.
    try {
      const r = await api.pageToSection(page, source);
      if (r?.source === "steamroller") navigate(VIEWS.steamrollerSection(r.section_id));
      else navigate(VIEWS.rulebookSection(r.section_id));
    } catch (e) {
      toast.info(`Page ${page} isn't inside any indexed ${source === "steamroller" ? "Steamroller" : "rulebook"} section.`);
    }
  };

  const out = [];
  let last = 0;
  let m;
  let i = 0;
  // Re-create regex per render so its lastIndex is fresh.
  const re = new RegExp(PAGE_REGEX.source, PAGE_REGEX.flags);
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      out.push(<span key={`t${i++}`}>{text.slice(last, m.index)}</span>);
    }
    const page = parseInt(m[1], 10);
    const inside = pagesSet.has(page);
    const outOfPdf = pdfTotalPages != null && (page < 1 || page > pdfTotalPages);

    if (outOfPdf) {
      // Page is referenced in text but doesn't exist in the loaded PDF
      // (abridged edition). Render as muted bold so it's clear it's a
      // reference but not actionable.
      out.push(
        <span
          key={`p${i++}`}
          className="font-medium text-muted-foreground/80"
          data-testid={`page-link-${page}`}
          data-inside="false"
          data-out-of-pdf="true"
          title={`page ${page} is not in the loaded PDF (only ${pdfTotalPages} pages)`}
        >
          {m[0]}
        </span>
      );
    } else {
      out.push(
        <button
          key={`p${i++}`}
          type="button"
          onClick={(e) => { e.stopPropagation(); handleClick(page); }}
          className={
            "inline px-1 -mx-0.5 rounded border leading-tight cursor-pointer transition " +
            (inside
              ? "text-emerald-300 border-emerald-700/50 bg-emerald-950/30 hover:bg-emerald-900/40"
              : "text-amber-300 border-amber-700/50 bg-amber-950/30 hover:bg-amber-900/40")
          }
          data-testid={`page-link-${page}`}
          data-inside={inside ? "true" : "false"}
          title={inside ? `Jump to page ${page}` : `Open the section that contains page ${page}`}
        >
          {m[0]}
        </button>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    out.push(<span key={`t${i++}`}>{text.slice(last)}</span>);
  }
  return <span className={className}>{out}</span>;
}
